import React, { useMemo } from "react";

import { SURVEY_QUESTION_TYPES } from "@performyard/models";
import { Crossfade, Skeleton, Text } from "@performyard/py-components";
import { GetSurveyParticipantResponseResponseJson } from "@performyard/service-contracts";

import { useGetSurveyParticipantResponse } from "../../../../../../../../../../api/hooks";

type SurveyResponseProps = {
	surveyId: string;
	participantId?: string | null;
};

const QuestionTypeToNameMap = {
	[SURVEY_QUESTION_TYPES.Instructions]: "Instructions",
	[SURVEY_QUESTION_TYPES.RatingScale]: "Rating Scale",
	[SURVEY_QUESTION_TYPES.Text]: "Long Answer",
	[SURVEY_QUESTION_TYPES.MultipleChoice]: "Multiple Choice",
	[SURVEY_QUESTION_TYPES.MultipleSelect]: "Multiple Choice (Supports Multiple Selections)",
};

const Instructions = ({ question }: { question: GetSurveyParticipantResponseResponseJson["answers"][0] }) => {
	return (
		<div className="flex flex-col gap-2">
			{question.questionText && (
				<Text size="paragraph-1" weight="semi-bold" color="primary-5">
					{question.questionText}
				</Text>
			)}
			{question.questionSubtitle && (
				<Text size="paragraph-3" weight="regular" color="neutral-7">
					{question.questionSubtitle}
				</Text>
			)}
		</div>
	);
};

const deriveAnswerTextFromValue = (
	value: string | null,
	options: GetSurveyParticipantResponseResponseJson["answers"][0]["options"]
): string => {
	if (options?.length) {
		const option = options.find((option) => option._id === value);
		return option?.text ?? "";
	}
	return value ?? "";
};

const Question = ({ question }: { question: GetSurveyParticipantResponseResponseJson["answers"][0] }) => {
	return (
		<div>
			<Text size="caption" weight="regular" color="neutral-5">
				{QuestionTypeToNameMap[question.questionType]}
			</Text>
			{question.questionText && (
				<div className="mb-2">
					<Text size="paragraph-2" weight="semi-bold">
						{question.questionText}
					</Text>
				</div>
			)}
			{question.questionSubtitle && (
				<div className="mb-2">
					<Text size="paragraph-3" color="neutral-7">
						{question.questionSubtitle}
					</Text>
				</div>
			)}
			{Array.isArray(question.value) ? (
				<ul className="flex min-h-4 flex-col gap-2 border-l-4 border-solid border-primary-5 py-2 pl-4">
					{question.value.map((value) => (
						<li key={value}>
							<Text size="paragraph-3" color="neutral-9">
								{deriveAnswerTextFromValue(value, question.options)}
							</Text>
						</li>
					))}
				</ul>
			) : (
				<div className="min-h-4 border-l-4 border-solid border-primary-5 py-2 pl-4">
					<Text size="paragraph-3" color="neutral-9">
						{deriveAnswerTextFromValue(question.value, question.options)}
					</Text>
				</div>
			)}
		</div>
	);
};

const LoadingSurveyResponse = () => {
	return (
		<section className="flex flex-col gap-6 overflow-y-auto bg-neutral-0 p-10">
			{Array.from({ length: 5 }).map((_, i) => (
				<div key={i}>
					<Skeleton className="mb-2 h-6 w-64" />
					<Skeleton className="mb-3 h-4 w-48" />
					<div className="border-l-4 border-solid border-neutral-2 py-2 pl-4">
						<Skeleton className="h-4 w-full" />
					</div>
				</div>
			))}
		</section>
	);
};

const EmptySurveyResponse = () => {
	return (
		<div className="flex h-64 items-center justify-center">
			<Text size="paragraph-2" color="neutral-5">
				No survey response found
			</Text>
		</div>
	);
};

const SurveyResponseContent = ({ surveyResponse }: { surveyResponse: GetSurveyParticipantResponseResponseJson }) => {
	return (
		<section className="flex flex-col gap-6 rounded-1 bg-neutral-0 p-10">
			{surveyResponse.answers.map((answer) => {
				if (answer.questionType === "instructions") {
					return <Instructions question={answer} key={answer.questionId} />;
				}
				return <Question question={answer} key={answer.questionId} />;
			})}
		</section>
	);
};

const SurveyResponse = ({ surveyId, participantId }: SurveyResponseProps) => {
	const { data: surveyResponse, isLoading } = useGetSurveyParticipantResponse(surveyId, participantId ?? "");

	const { viewKey, view } = useMemo(() => {
		if (!participantId || isLoading) {
			return { viewKey: "loading", view: <LoadingSurveyResponse /> };
		}
		if (!surveyResponse) {
			return { viewKey: "empty", view: <EmptySurveyResponse /> };
		}
		return {
			viewKey: `response-${participantId}`,
			view: <SurveyResponseContent surveyResponse={surveyResponse} />,
		};
	}, [participantId, isLoading, surveyResponse]);

	return <Crossfade viewKey={viewKey}>{view}</Crossfade>;
};

export default SurveyResponse;
